import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

// Crear instancia de axios para questions
const questionsApi = axios.create({
  baseURL: `${API_BASE_URL}/questions`,
  timeout: 10000,
})

// Interceptor para agregar token de autenticación
questionsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Interceptor para manejo de respuestas
questionsApi.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    // Manejo de errores de autenticación
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }

    return Promise.reject(error)
  },
)

// Helper para crear FormData para preguntas con imágenes
const createQuestionFormData = (questionData) => {
  const formData = new FormData()

  // Campos básicos
  formData.append('statement', questionData.statement)
  formData.append('justification', questionData.justification)
  formData.append('area', questionData.area)
  formData.append('time_limit', questionData.time_limit)
  formData.append('school_grade_id', questionData.school_grade_id)
  formData.append('school_subject_id', questionData.school_subject_id)

  // Imagen del enunciado (opcional)
  if (questionData.statement_image) {
    formData.append('statement_image', questionData.statement_image)
  }

  // Imagen de la justificación (opcional)
  if (questionData.justification_image) {
    formData.append('justification_image', questionData.justification_image)
  }

  // Opciones - formato normalizado para la nueva API
  questionData.options.forEach((option, index) => {
    formData.append(`options[${index}][label]`, option.label)
    // Backend expects 1/0 instead of true/false
    formData.append(`options[${index}][is_correct]`, option.is_correct ? '1' : '0')

    if (option.id) {
      formData.append(`options[${index}][id]`, option.id)
    }

    if (option.text) {
      formData.append(`options[${index}][text]`, option.text)
    }

    if (option.image) {
      formData.append(`options[${index}][image]`, option.image)
    }
  })

  return formData
}

export const questionService = {
  // Obtener lista de preguntas
  getQuestions: (params = {}) => {
    return questionsApi.get('', { params })
  },

  // Obtener pregunta por ID
  getQuestion: (id) => {
    return questionsApi.get(`/${id}`)
  },

  // Crear nueva pregunta
  createQuestion: (questionData, hasFiles = false) => {
    const headers = hasFiles
      ? { 'Content-Type': 'multipart/form-data' }
      : { 'Content-Type': 'application/json' }
    return questionsApi.post('', questionData, { headers })
  },

  // Actualizar pregunta
  updateQuestion: (id, questionData, hasFiles = false) => {
    if (hasFiles) {
      // PHP cannot parse files from PUT multipart requests; use POST with _method spoofing
      questionData.append('_method', 'PUT')
      return questionsApi.post(`/${id}`, questionData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }
    return questionsApi.put(`/${id}`, questionData, {
      headers: { 'Content-Type': 'application/json' },
    })
  },

  // Eliminar pregunta
  deleteQuestion: (id) => {
    return questionsApi.delete(`/${id}`)
  },

  // Responder pregunta (solo para estudiantes)
  answerQuestion: (id, answerData) => {
    return questionsApi.post(`/${id}/answer`, answerData)
  },

  // Obtener imagen de pregunta (con autenticación)
  getQuestionImage: (encodedFilename) => {
    return questionsApi.get(`/images/${encodedFilename}`, {
      responseType: 'blob',
    })
  },

  // Obtener preguntas por grado escolar
  // Para estudiantes: automáticamente obtiene la pregunta activa de su grupo en sesión
  // Para admin/teacher: debe pasar pin_code como parámetro
  getQuestionsBySchoolGrade: (schoolGradeId, pinCode = null) => {
    const params = {}
    if (pinCode) {
      params.pin_code = pinCode
    }

    return axios
      .get(`${API_BASE_URL}/school-grades/${schoolGradeId}/questions`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      })
      .then((response) => response.data)
  },
}

export { createQuestionFormData }
export default questionsApi
